import React, { Component } from "react";
import { apiUrl } from "../../config";
import { renderHelpMarkdown } from "./helpMarkdownRenderer";

class HelpDrawer extends Component {
  state = {
    manifest: null,
    title: "Help",
    content: "",
    currentDocPath: null,
    routeDocPath: null,
    loading: false,
    error: null,
  };

  componentDidMount() {
    this.loadManifest();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.isOpen && this.props.isOpen) {
      if (this.props.requestedDocPath) {
        this.loadDoc(this.props.requestedDocPath);
      } else {
        this.loadCurrentDoc();
      }
    }
    if (
      this.props.isOpen &&
      this.props.requestedDocPath &&
      prevProps.requestedDocPath !== this.props.requestedDocPath
    ) {
      this.loadDoc(this.props.requestedDocPath);
    }
    if (prevState.content !== this.state.content && this.state.content) {
      this.scheduleMermaidRender();
    }
  }

  scheduleMermaidRender() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.renderMermaidDiagrams());
    });
  }

  renderMermaidDiagrams() {
    const nodes = document.querySelectorAll(".help-drawer .mermaid:not([data-processed])");
    if (!nodes.length) return;

    this.loadMermaid()
      .then(mermaid => {
        mermaid.initialize({
          startOnLoad: false,
          theme: this.getMermaidTheme(),
          securityLevel: "loose",
        });

        if (mermaid.run) {
          return mermaid.run({ nodes });
        }
        if (mermaid.init) {
          mermaid.init(undefined, nodes);
        }
      })
      .catch(() => {});
  }

  getMermaidTheme() {
    const theme = document.documentElement.getAttribute("data-theme");
    return theme === "dark" ? "dark" : "neutral";
  }

  loadMermaid() {
    if (window.mermaid) return Promise.resolve(window.mermaid);
    if (window.__mbossMermaidPromise) return window.__mbossMermaidPromise;

    window.__mbossMermaidPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js";
      script.async = true;
      script.onload = () => resolve(window.mermaid);
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return window.__mbossMermaidPromise;
  }

  getAppName() {
    if (this.props.appName) return this.props.appName;
    return (this.props.appBase || "").replace(/^\/+/, "") || "client";
  }

  getAuthHeaders() {
    const token = this.props.getAuthToken ? this.props.getAuthToken() : null;
    if (!token) return {};

    return {
      Authorization: `Bearer ${token}`,
      "x-auth-token": token,
    };
  }

  async loadManifest() {
    try {
      const response = await fetch(
        `${apiUrl}/docs/${this.getAppName()}/manifest.json`,
        { headers: this.getAuthHeaders() }
      );
      if (!response.ok) throw new Error("Missing help manifest");
      const manifest = await response.json();
      this.setState({ manifest }, () => {
        if (this.props.isOpen) this.loadCurrentDoc();
      });
    } catch (error) {
      this.setState({ error: "Help content is not available yet." });
    }
  }

  getCurrentDocPath() {
    const { manifest } = this.state;
    if (!manifest) return null;

    const currentPath = window.location.pathname;

    const matchedRoute = Object.keys(manifest.routes || {})
      .sort((a, b) => b.length - a.length)
      .find(route => currentPath === route || currentPath.indexOf(`${route}/`) === 0);

    return matchedRoute ? manifest.routes[matchedRoute] : manifest.defaultDoc;
  }

  async loadCurrentDoc() {
    const docPath = this.getCurrentDocPath();
    if (!docPath) return;

    this.setState({ routeDocPath: docPath }, () => this.loadDoc(docPath));
  }

  async loadDoc(docPath) {
    this.setState({ loading: true, error: null });

    try {
      const response = await fetch(`${apiUrl}/docs/${docPath}`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Missing help file");
      const content = await response.text();
      const title = this.extractTitle(content);
      this.setState({ content, currentDocPath: docPath, title, loading: false }, () => {
        this.scheduleMermaidRender();
      });
    } catch (error) {
      this.setState({
        content: "",
        title: "Help",
        loading: false,
        error: "Help content is not available for this page.",
      });
    }
  }

  extractTitle(content) {
    const heading = content.split("\n").find(line => line.indexOf("# ") === 0);
    return heading ? heading.replace("# ", "").trim() : "Help";
  }

  isExternalLink(href) {
    return /^(https?:|mailto:|tel:)/i.test(href);
  }

  normalizeDocLink(href) {
    if (!href || this.isExternalLink(href) || href.indexOf("#") === 0) return null;

    const appName = this.getAppName();
    const cleanHref = href.split("#")[0].split("?")[0];
    if (!cleanHref || cleanHref.indexOf(".md") === -1) return null;

    const currentDocPath = this.state.currentDocPath || this.getCurrentDocPath() || `${appName}/landing.md`;
    const baseParts = currentDocPath.split("/").slice(0, -1);
    const linkParts = cleanHref.replace(/^\/+/, "").split("/").filter(Boolean);
    const parts = cleanHref.indexOf("/") === 0 || cleanHref.indexOf(`${appName}/`) === 0
      ? []
      : baseParts;

    linkParts.forEach(part => {
      if (part === ".") return;
      if (part === "..") {
        parts.pop();
        return;
      }
      parts.push(part);
    });

    const normalizedPath = parts.join("/");
    return normalizedPath.indexOf(`${appName}/`) === 0 ? normalizedPath : null;
  }

  handleDocLink(event, href) {
    event.preventDefault();
    const docPath = this.normalizeDocLink(href);
    if (docPath) this.loadDoc(docPath);
  }

  goToHome() {
    const { manifest } = this.state;
    if (manifest && manifest.defaultDoc) this.loadDoc(manifest.defaultDoc);
  }

  goToRouteDoc() {
    const { routeDocPath } = this.state;
    if (routeDocPath) this.loadDoc(routeDocPath);
  }

  renderInline(text) {
    const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);

    return parts.map((part, index) => {
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const label = linkMatch[1];
        const href = linkMatch[2];

        if (this.isExternalLink(href)) {
          return <a key={index} href={href} target="_blank" rel="noopener noreferrer">{label}</a>;
        }

        const docPath = this.normalizeDocLink(href);
        if (!docPath) return <React.Fragment key={index}>{label}</React.Fragment>;

        return <a key={index} href={`/${docPath}`} onClick={event => this.handleDocLink(event, href)}>{label}</a>;
      }
      if (part.indexOf("**") === 0 && part.lastIndexOf("**") === part.length - 2) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      if (part.indexOf("`") === 0 && part.lastIndexOf("`") === part.length - 1) {
        return <code key={index}>{part.slice(1, -1)}</code>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  }

  renderMarkdown(content) {
    return renderHelpMarkdown(content, {
      renderInline: text => this.renderInline(text),
      keyPrefix: this.state.currentDocPath || "help",
    });
  }

  render() {
    const { isOpen, onClose } = this.props;
    const { content, currentDocPath, error, loading, manifest, routeDocPath, title } = this.state;

    if (!isOpen) return null;

    const homeDocPath = manifest && manifest.defaultDoc;

    return (
      <React.Fragment>
        <div className="help-drawer-backdrop" onClick={onClose} />
        <aside className="help-drawer" aria-label="Application help">
          <div className="help-drawer-header">
            <div>
              <span className="help-drawer-eyebrow">MBOSS Help</span>
              <h2>{title}</h2>
            </div>
            <button className="help-drawer-close" type="button" onClick={onClose} aria-label="Close help">
              <i className="fa fa-times" />
            </button>
          </div>
          <div className="help-doc-nav" aria-label="Help navigation">
            <button
              className="help-doc-nav-button"
              type="button"
              onClick={() => this.goToHome()}
              disabled={!homeDocPath || currentDocPath === homeDocPath}
            >
              Home
            </button>
            <button
              className="help-doc-nav-button"
              type="button"
              onClick={() => this.goToRouteDoc()}
              disabled={!routeDocPath || currentDocPath === routeDocPath}
            >
              Page help
            </button>
          </div>
          <div className="help-drawer-body">
            {loading && <p>Loading help...</p>}
            {error && <p>{error}</p>}
            {!loading && !error && this.renderMarkdown(content)}
          </div>
        </aside>
      </React.Fragment>
    );
  }
}

export default HelpDrawer;
